import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { waLink, track } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";

const NAV = [
  { label: "Product", href: "/#platform" },
  { label: "Solutions", href: "/solutions/training-organizations" },
  { label: "Pricing", href: "/pricing" },
  { label: "Resources", href: "/blog" },
  { label: "FAQ", href: "/faq" },
  { label: "Attendance Login", href: "/attendance" },
];

export const Logo = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
    <span className="w-9 h-9 rounded-xl bg-emerald-700 text-white flex items-center justify-center font-bold text-lg font-display">M</span>
    <span className="font-display font-bold text-lg text-slate-900 leading-none">
      Mashara <span className="text-emerald-700">Finance</span>
    </span>
  </Link>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  useSite();
  const navigate = useNavigate();

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1" data-testid="main-nav">
            {NAV.map((item) => (
              <Link key={item.label} to={item.href} data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-emerald-700 transition-colors rounded-lg hover:bg-emerald-50">
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-2">
            <a href={waLink("Hello, I want to know more about Mashara Finance and its pricing.")}
              target="_blank" rel="noopener noreferrer" data-testid="header-whatsapp-button"
              onClick={() => track("whatsapp_click", { location: "header" })}
              className="p-2 text-slate-500 hover:text-emerald-700 transition-colors" aria-label="WhatsApp">
              <MessageCircle className="w-5 h-5" />
            </a>
            {user ? (
              <Button data-testid="header-dashboard-button" onClick={() => navigate(user.role === "admin" ? "/admin" : "/app")}
                variant="outline" className="rounded-xl border-slate-300">
                Dashboard
              </Button>
            ) : (
              <Button data-testid="header-login-button" variant="ghost" onClick={() => navigate("/login")}
                className="text-slate-700 hover:text-emerald-700">
                Login
              </Button>
            )}
            <Button data-testid="header-trial-button" onClick={() => { track("hero_cta_click", { location: "header" }); navigate("/signup"); }}
              className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white">
              Start Free Trial
            </Button>
          </div>
          <button data-testid="mobile-menu-button" className="lg:hidden p-2 text-slate-700" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-1" data-testid="mobile-menu">
          {NAV.map((item) => (
            <Link key={item.label} to={item.href} onClick={() => setOpen(false)}
              className="block px-3 py-3 text-base font-medium text-slate-700 hover:bg-emerald-50 rounded-lg">
              {item.label}
            </Link>
          ))}
          <div className="flex gap-2 pt-3">
            <Button variant="outline" className="flex-1 rounded-xl" onClick={() => { setOpen(false); navigate("/login"); }} data-testid="mobile-login-button">Login</Button>
            <Button className="flex-1 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white"
              onClick={() => { setOpen(false); navigate("/signup"); }} data-testid="mobile-trial-button">Start Free Trial</Button>
          </div>
        </div>
      )}
    </header>
  );
}
